import { useCallback, useEffect, useState } from 'react';

/**
 * Prefixo versionado da chave de sessão que marca que o modal de Promoções já
 * foi exibido para uma loja nesta sessão do navegador. A versão (`v1`) permite
 * invalidar o comportamento no futuro sem colidir com valores antigos gravados
 * no `sessionStorage`.
 */
const SEEN_STORAGE_PREFIX = 'larah:promotions-modal-seen:v1';

/**
 * Monta a chave de `sessionStorage` escopada por loja. Retorna `null` quando o
 * identificador da loja é ausente/vazio — sem loja não há o que marcar, e um
 * default nulo evita gravar uma chave "órfã" compartilhada entre lojas.
 */
function buildSeenKey(storeKey: string | undefined | null): string | null {
  if (typeof storeKey !== 'string') return null;
  const trimmed = storeKey.trim();
  if (!trimmed) return null;
  return `${SEEN_STORAGE_PREFIX}:${trimmed}`;
}

/**
 * Lê, de forma defensiva, se o modal já foi exibido para esta loja na sessão.
 *
 * `sessionStorage` pode lançar (modo privativo/embutido, cookies bloqueados);
 * em qualquer falha o default seguro é "ainda não visto" para não suprimir o
 * modal silenciosamente.
 */
function hasSeenPromotionsModal(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** Persiste (best-effort) que o modal já foi exibido para esta loja na sessão. */
function markPromotionsModalSeen(key: string): void {
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    // Sem storage disponível: aceitamos que o modal possa reaparecer numa
    // próxima montagem. É um degrade benigno, sem impacto de segurança.
  }
}

interface UsePromotionsModalSessionParams {
  /**
   * Identificador da loja (tipicamente o `slug`) usado para escopar a marcação
   * "já visto" por loja — cada estabelecimento tem direito a exibir o modal uma
   * vez por sessão, de forma independente dos demais.
   */
  storeKey: string | undefined | null;
  /**
   * Habilita a abertura. Serve para adiar a decisão até que exista, de fato, o
   * que exibir (ex.: promoções válidas já derivadas do cardápio carregado).
   * Enquanto `false`, o modal permanece fechado e nada é marcado como visto.
   */
  enabled: boolean;
}

interface UsePromotionsModalSessionResult {
  /** Verdadeiro quando o modal deve estar aberto agora. */
  isOpen: boolean;
  /** Fecha o modal na sessão atual (não reabre para a mesma loja na sessão). */
  close: () => void;
}

/**
 * Controla *quando* o modal de Promoções aparece, garantindo que ele interrompa
 * o usuário no máximo **uma vez por sessão do navegador e por loja**.
 *
 * A decisão é escopada pela chave da loja em `sessionStorage`: ao abrir para uma
 * loja, marca-se "visto" para aquela chave, de modo que re-renders, re-visitas à
 * tela da loja ou remontagens dentro da mesma sessão não reabram o modal. Trocar
 * de loja (nova `storeKey` ainda não vista) volta a habilitar a exibição.
 *
 * A abertura só ocorre quando `enabled` é verdadeiro, permitindo que o consumidor
 * aguarde a existência de promoções válidas antes de exibir (default seguro: sem
 * `enabled`, nada abre e nada é gravado).
 */
export function usePromotionsModalSession({
  storeKey,
  enabled,
}: UsePromotionsModalSessionParams): UsePromotionsModalSessionResult {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const key = buildSeenKey(storeKey);
    if (!key || hasSeenPromotionsModal(key)) return;

    // Marca "visto" no ato da abertura para que a decisão seja idempotente:
    // qualquer reavaliação posterior deste efeito (inclusive a dupla execução do
    // StrictMode) enxerga a marcação e não reabre.
    markPromotionsModalSeen(key);
    setIsOpen(true);
  }, [enabled, storeKey]);

  const close = useCallback(() => setIsOpen(false), []);

  return { isOpen, close };
}
